import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { ResumeIndicatorB5ByHealthTeamData } from "@/services/esus/indicator/esb/b5/getResumeIndicatorB5ByHealthTeam";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getIndicatorClassification, IndicatorEsbEnum } from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { IndicatorEsbB5HealthTeamsFilters } from "./IndicatorEsbB5HealthTeamsFilters";
import { IndicatorEsbB5HealthTeamsClassificationTable } from "./IndicatorEsbB5HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB5HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorB5ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorB5ByHealthTeam: () => Promise<ResumeIndicatorB5ByHealthTeamData[]>;
};

export function IndicatorEsbB5HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorB5ByHealthTeam,
}: Readonly<IndicatorEsbB5HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorB5ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorB5AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Procedimentos por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB5AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorB5ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe ESB": healthTeamClassification.esbHealthTeamName,
					"Equipe ESB INE": healthTeamClassification.esbHealthTeamIne,
					"Equipe ESF": healthTeamClassification.linkedHealthTeamName,
					"Equipe ESF INE": healthTeamClassification.linkedHealthTeamIne,
					"Procedimentos preventivos em cidadãos vinculados":
						healthTeamClassification.totalPreventProceduresLinked ?? 0,
					"Procedimentos preventivos em cidadãos não vinculados":
						healthTeamClassification.totalPreventProcedures -
						healthTeamClassification.totalPreventProceduresLinked,
					"Procedimentos preventivos": healthTeamClassification.totalPreventProcedures,
					"Procedimentos gerais em cidadãos vinculados":
						healthTeamClassification.totalProceduresLinked ?? 0,
					"Procedimentos gerais em cidadãos não vinculados":
						healthTeamClassification.totalProcedures -
						healthTeamClassification.totalProceduresLinked,
					"Procedimentos gerais": healthTeamClassification.totalProcedures,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalPreventProcedures,
						healthTeamClassification.totalProcedures
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsbEnum.B5,
						calculatePercentage(
							healthTeamClassification.totalPreventProcedures,
							healthTeamClassification.totalProcedures
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsbB5HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB5HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
